import React from 'react';
import { useTranslation } from 'react-i18next';

interface BedroomCardProps {
    title: string;
    floor: string;
    features: string[];
    imagePlaceholder?: string;
}

const BedroomCard: React.FC<BedroomCardProps> = ({ title, floor, features, imagePlaceholder = "Bedroom Photo" }) => {
    return (
        <div className="section-card shadow-layered hover:shadow-layered-lg transition-shadow">
            <div className="mb-4">
                {/* Placeholder for bedroom photo */}
                <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400">
                    {imagePlaceholder}
                </div>
            </div>
            <h3 className="text-2xl font-bold text-terracotta mb-3">{title}</h3>
            <p className="text-sm text-gray-500 mb-3">{floor}</p>
            <ul className="space-y-2 text-gray-700">
                {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                        <span className="text-terracotta mt-1">✓</span>
                        <span>{feature}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const BedroomsSection: React.FC = () => {
    const { t } = useTranslation();

    const bedrooms = [
        {
            title: t('bedrooms.master.title'),
            floor: t('bedrooms.master.floor'),
            features: [
                t('bedrooms.master.features.0'),
                t('bedrooms.master.features.1'),
                t('bedrooms.master.features.2')
            ]
        },
        {
            title: t('bedrooms.poolTerrace1.title'),
            floor: t('bedrooms.poolTerrace1.floor'),
            features: [
                t('bedrooms.poolTerrace1.features.0'),
                t('bedrooms.poolTerrace1.features.1'),
                t('bedrooms.poolTerrace1.features.2')
            ]
        },
        {
            title: t('bedrooms.poolTerrace2.title'),
            floor: t('bedrooms.poolTerrace2.floor'),
            features: [
                t('bedrooms.poolTerrace2.features.0'),
                t('bedrooms.poolTerrace2.features.1'),
                t('bedrooms.poolTerrace2.features.2')
            ]
        },
        {
            title: t('bedrooms.gardenLevel1.title'),
            floor: t('bedrooms.gardenLevel1.floor'),
            features: [
                t('bedrooms.gardenLevel1.features.0'),
                t('bedrooms.gardenLevel1.features.1'),
                t('bedrooms.gardenLevel1.features.2')
            ]
        },
        {
            title: t('bedrooms.gardenLevel2.title'),
            floor: t('bedrooms.gardenLevel2.floor'),
            features: [
                t('bedrooms.gardenLevel2.features.0'),
                t('bedrooms.gardenLevel2.features.1'),
                t('bedrooms.gardenLevel2.features.2')
            ]
        }
    ];

    return (
        <section id="bedrooms" className="py-24 px-6 bg-warm-white">
            <div className="container">
                <h2 className="heading-main text-center mb-4">{t('bedrooms.title')}</h2>
                <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
                    {t('bedrooms.subtitle')}
                </p>
                <div className="divider-geometric"></div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
                    {bedrooms.map((bedroom, index) => (
                        <BedroomCard
                            key={index}
                            title={bedroom.title}
                            floor={bedroom.floor}
                            features={bedroom.features}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default BedroomsSection;
